<template>
  <!-- The standalone /login page renders the same onboarding pages as the modal, in
       the same dialog look: a 460 px card under the site header, with no Close since
       there is nothing to close (OnBoardingFrame hides it in standalone mode). -->
  <div class="login-page">
    <div class="login-card">
      <div v-if="isLoading && !currentPageIsForm" class="redirect-state">
        <span class="spinner"></span>
        <p>{{ $t('login.app.signing_you_in') }}</p>
      </div>
      <OnBoardingPageRouter v-else />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { OnBoardingGoal } from '../../utils/models/OnBoardingGoal';
import { OnBoardingPage } from '../../utils/models/OnBoardingPage';
import OnBoardingPageRouter from '../on_boarding/components/OnBoardingPageRouter.vue';
import { auth, onAuthStateChanged, warmUpAuthPopups } from '../../utils/firebase/auth';
import { isSignInWithEmailLink } from 'firebase/auth';

export default defineComponent({
  name: 'LoginApp',
  components: {
    OnBoardingPageRouter,
  },
  data() {
    return {
      authListenerUnsubscribe: null as null | (() => void),
    };
  },
  computed: {
    topPage() {
      return this.$store.getters['onBoarding/lastPage'];
    },
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
    currentPageIsForm(): boolean {
      // While the user is interacting with a form, keep showing it under the
      // overlay rather than swapping in the "redirecting…" state.
      return this.topPage === OnBoardingPage.EmailLogin
        || this.topPage === OnBoardingPage.EmailSignUp
        || this.topPage === OnBoardingPage.MagicLink;
    },
  },
  created() {
    // Set initial store state BEFORE first render.
    this.$store.commit('onBoarding/setStandaloneMode', true);
    this.$store.commit('onBoarding/setOnBoardingGoal', OnBoardingGoal.CreateAccount);
    // Email-link callback: skip the SignUp landing page and route straight
    // into the MagicLink view, which renders the verifying / re-enter-email
    // state while signInWithEmailLink runs.
    if (isSignInWithEmailLink(auth, window.location.href)) {
      this.$store.commit('onBoarding/setPageStack', [OnBoardingPage.MagicLink]);
    } else {
      this.$store.commit('onBoarding/setPageStack', [OnBoardingPage.SignUpOptions]);
    }
  },
  mounted() {
    // The standalone /login page shows the sign-in options straight away, so
    // build Firebase's popup iframe now rather than on the first tap.
    void warmUpAuthPopups();
    if (isSignInWithEmailLink(auth, window.location.href)) {
      // Completion action handles its own post-sign-in redirect via
      // setNextPostLoginPage, so we skip the generic onAuthStateChanged
      // listener below — otherwise the two would race to navigate away.
      this.$store.dispatch('onBoarding/completeMagicLinkSignIn');
      return;
    }
    // If the user lands on /login already authenticated, bounce them through
    // the same /firebase/auth handshake the old page used. Preserves the
    // existing redirect-after-login behaviour.
    //
    // `authInProgress` is the guard that keeps this from also firing for a
    // sign-in happening *on* this page. Firebase signs the user in before the
    // store records them, so without it this listener wins the race against the
    // flow's own routing and navigates away — which sent every brand-new
    // account straight to the dashboard, skipping the signup steps entirely.
    // The magic-link branch above returns early for the same reason.
    this.authListenerUnsubscribe = onAuthStateChanged(auth, (user) => {
      const state = this.$store.state.onBoarding;
      if (user && !state.user && !state.authInProgress) {
        user.getIdToken().then((token) => {
          window.location.assign(`/firebase/auth?accessToken=${token}`);
        });
      }
    });
  },
  beforeUnmount() {
    if (this.authListenerUnsubscribe) {
      this.authListenerUnsubscribe();
    }
    this.$store.commit('onBoarding/setStandaloneMode', false);
  },
});
</script>

<style scoped>
.login-page {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 45px 16px 64px;
}

.login-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 460px;
  background: #1c1e22;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}
/* The page router's wrapper. */
.login-card > div {
  display: flex;
  flex-direction: column;
}

.redirect-state {
  align-items: center;
  gap: 12px;
  padding: 48px 32px;
  text-align: center;
  color: rgba(255, 255, 255, 0.85);
}
.redirect-state p {
  margin: 0;
}
.redirect-state .spinner {
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.18);
  border-top-color: var(--yellow, #fddf00);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Short laptop screens: keep the tallest step above the fold. */
@media (max-height: 820px) {
  .login-page { padding-top: 24px; }
}
</style>
